import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import AkSelect from '@atlaskit/select';

import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl/src/index.tsx';
import type { ChartData, SeriesDataEntity, Series } from '../../../common/summary/types.tsx';
import messages from './messages.tsx';
import { getSelectedTimePeriodMessage } from './utils.tsx';

type DIProps = {
	intl: IntlShape;
};

type Props = DIProps & {
	source: ChartData;
	selectedDrillDownPoint: SeriesDataEntity;
	onSwitchSeries: (arg1: SeriesDataEntity) => void;
	onSwitchTable: () => void;
};

type SeriesPickerOptionType = {
	label: string;
	value: Omit<Series, 'name'>;
};

export const SeriesPickerView = ({
	intl: { formatMessage },
	source: { chartSeries, searchBy },
	selectedDrillDownPoint,
	onSwitchSeries,
	onSwitchTable,
}: Props) => {
	const seriesPickerWithLabel = useMemo(() => {
		let seriesPicker;
		if (chartSeries.length === 1) {
			const { itemStyle, name } = chartSeries[0];
			seriesPicker = (
				<OptionWithHighlighter background={itemStyle.color}>{name}</OptionWithHighlighter>
			);
		} else {
			const selectableOptions: SeriesPickerOptionType[] = chartSeries.map(({ name, ...rest }) => ({
				label: name,
				value: rest,
			}));
			const selectedOption = selectableOptions.find(
				({ value: { seriesIndex } }) => seriesIndex === selectedDrillDownPoint.seriesIndex,
			);

			seriesPicker = (
				<AkSelect<SeriesPickerOptionType>
					value={selectedOption}
					options={selectableOptions}
					// @ts-expect-error - TS2322 - Type '{ readonly option: (styles: any, { value: { itemStyle: { color }, }, }: ItemStyleValue) => any; readonly singleValue: (styles: any, { data: { value: { itemStyle: { color }, }, }, }: {    data: ItemStyleValue;}) => any; readonly container: (styles: any) => any; }' is not assignable to type 'Partial<Styles<{ label: string; value: { [key: string]: string | number; type: string; typeKey?: string | undefined; seriesId: string; seriesIndex: number; showAllSymbol: boolean; summaryValue: YAxisData; ... 7 more ...; seriesType?: SeriesType | undefined; }; }, false, GroupTypeBase<...>>>'.
					styles={colourStyles}
					// @ts-expect-error - TS2339 - Property 'value' does not exist on type '{ label: string; value: { [key: string]: string | number; type: string; typeKey?: string | undefined; seriesId: string; seriesIndex: number; showAllSymbol: boolean; summaryValue: YAxisData; ... 7 more ...; seriesType?: SeriesType | undefined; }; } | null'.
					onChange={({ value: { seriesId, seriesIndex } }) => {
						onSwitchSeries({
							...selectedDrillDownPoint,
							seriesId,
							seriesIndex,
						});
					}}
				/>
			);
		}

		const timeInterval = getSelectedTimePeriodMessage(
			searchBy,
			selectedDrillDownPoint.expandedDate,
			formatMessage,
		);

		return (
			<>
				{/* eslint-disable-next-line jsx-a11y/label-has-for */}
				<InlineLabel as="label" for="react-select-2-input">
					{formatMessage(messages.seriesPickerDetailsLabel)}
				</InlineLabel>
				{seriesPicker}
				<InlineLabel>{timeInterval}</InlineLabel>
			</>
		);
	}, [chartSeries, formatMessage, onSwitchSeries, searchBy, selectedDrillDownPoint]);
	return (
		<Container data-test-id="servicedesk-reports-common.ui.table.series-picker.container">
			<SeriesPickerWrapper>{seriesPickerWithLabel}</SeriesPickerWrapper>
			<Button appearance="link" onClick={onSwitchTable}>
				{formatMessage(messages.returnToSummary)}
			</Button>
		</Container>
	);
};

export const SeriesPicker = injectIntl(SeriesPickerView);

type ItemStyleValue = {
	value: {
		itemStyle: {
			color: string;
		};
	};
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SeriesPickerWrapper = styled.div({
	flex: 1,
	display: 'flex',
	alignItems: 'center',
	marginRight: 'auto',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const OptionWithHighlighter = styled.span<{ background: any }>((props) => ({
	padding: token('space.100', '8px'),
	'&::before': {
		content: "''",
		display: 'inline-block',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		background: props.background,
		padding: token('space.100', '8px'),
		marginRight: token('space.100', '8px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		transform: `translateY(${gridSize * 0.375}px)`,
	},
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineLabel = styled.h4<{ for?: string }>({
	margin: 0,
});

const getOptionHighlighter = (color = '#ccc') => ({
	alignItems: 'center',
	display: 'flex',

	':before': {
		content: '" "',
		display: 'block',
		background: color,
		marginRight: `${gridSize}px`,
		padding: `${gridSize}px`,
	},
});

const colourStyles = {
	option: (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		styles: any,
		{
			value: {
				itemStyle: { color },
			},
		}: ItemStyleValue,
	) => ({ ...styles, ...getOptionHighlighter(color) }),
	singleValue: (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		styles: any,
		{
			data: {
				value: {
					itemStyle: { color },
				},
			},
		}: {
			data: ItemStyleValue;
		},
	) => ({
		...styles,
		...getOptionHighlighter(color),
	}),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	container: (styles: any) => ({ ...styles, padding: token('space.100', '8px'), width: '25%' }),
} as const;
